import clsx from 'clsx'
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import styles from './VerifiedCheck.module.css'

interface VerifiedCheckProps {
  /** sm beside a handle in a row or byline; md beside the member page's h1. */
  size?: 'sm' | 'md'
  className?: string
}

const LABEL = 'Signed-in owner'
const EXPLAIN =
  'This member signed in to publish, so their reviews count for points and show on their profile.'

/** Popover geometry: never wider than the viewport minus a gutter, flips below near the top. */
const MAX_WIDTH = 240
const GUTTER = 8
const GAP = 8
const FLIP_BELOW = 84

/** Zero-width WORD JOINER: forbids a line break between the handle's last letter and the check. */
const JOIN = '⁠'

interface Place {
  top: number
  left: number
  width: number
  arrow: number
  below: boolean
}

// SSR renders the closed popover only, so the layout effect never runs on the server.
const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

/**
 * The X-style check after a member's handle: a brand-blue disc with a check mark. It means one
 * thing — the author is a signed-in member (`author.member !== null`). An anonymous owner never
 * wears it, and the owner badge carries no tick of its own, so one mark has one meaning. The
 * label names no sign-in provider. Render it right after the handle's link (beside it, never inside:
 * a button cannot live in an anchor): a word joiner glues it to the last letter, so it never wraps onto a line of its own.
 *
 * It explains itself: hover or keyboard focus opens a small popover, a click or tap toggles it
 * (without following the author link it usually sits in), Escape or a tap elsewhere closes it.
 * The popover is pure client UI — the server HTML is the same for every reader (closed). It is
 * `position: fixed` so the leaderboard's scrolling table or a clipped card never cuts it off.
 */
const VerifiedCheck: React.VFC<VerifiedCheckProps> = ({ size = 'sm', className }) => {
  const tipId = useId()
  const wrapRef = useRef<HTMLSpanElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const lastPointer = useRef<string>('')
  const [open, setOpen] = useState(false)
  const [place, setPlace] = useState<Place | null>(null)

  const measure = useCallback(() => {
    const button = buttonRef.current
    if (!button) {
      return
    }
    const rect = button.getBoundingClientRect()
    const viewport = document.documentElement.clientWidth
    const width = Math.min(MAX_WIDTH, viewport - 2 * GUTTER)
    const center = rect.left + rect.width / 2
    const left = Math.min(Math.max(center - width / 2, GUTTER), viewport - GUTTER - width)
    const below = rect.top < FLIP_BELOW
    setPlace({
      top: below ? rect.bottom + GAP : rect.top - GAP,
      left,
      width,
      arrow: center - left,
      below,
    })
  }, [])

  useIsoLayoutEffect(() => {
    if (open) {
      measure()
    } else {
      setPlace(null)
    }
  }, [open, measure])

  // One set of document listeners, only while open: Escape, a tap outside, scroll/resize.
  useEffect(() => {
    if (!open) {
      return undefined
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    const onPointer = (e: PointerEvent): void => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    window.addEventListener('scroll', measure, { passive: true, capture: true })
    window.addEventListener('resize', measure)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
      window.removeEventListener('scroll', measure, { capture: true })
      window.removeEventListener('resize', measure)
    }
  }, [open, measure])

  const stop = (e: React.MouseEvent): void => {
    // The check sits beside links, never inside one. A click or tap on it only explains: it must never reach a surrounding link or a hover-card trigger.
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <span ref={wrapRef} className={clsx(styles.wrap, className)}>
      {JOIN}
      <button
        ref={buttonRef}
        type="button"
        className={clsx(styles.check, styles[`check_${size}`])}
        aria-label={LABEL}
        aria-expanded={open}
        aria-describedby={tipId}
        onPointerDown={e => {
          lastPointer.current = e.pointerType
        }}
        onPointerEnter={e => {
          if (e.pointerType === 'mouse') {
            setOpen(true)
          }
        }}
        onPointerLeave={e => {
          if (e.pointerType === 'mouse') {
            setOpen(false)
          }
        }}
        onFocus={e => {
          if (e.currentTarget.matches(':focus-visible')) {
            setOpen(true)
          }
        }}
        onBlur={() => setOpen(false)}
        onClick={e => {
          stop(e)
          // A mouse click lands on an already-hovered check: keep it open. Touch and keyboard toggle.
          if (lastPointer.current === 'mouse') {
            setOpen(true)
          } else {
            setOpen(v => !v)
          }
          lastPointer.current = ''
        }}
      >
        <svg className={styles.svg} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="12" fill="currentColor" />
          <path className={styles.mark} d="M7 12.4l3.3 3.3L17.2 8.8" fill="none" />
        </svg>
      </button>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- a click on the open popover must not follow the author link around it */}
      <span
        id={tipId}
        role="tooltip"
        hidden={!open || !place}
        className={clsx(styles.tip, place?.below ? styles.tip_below : styles.tip_above)}
        style={
          place
            ? ({
                top: place.top,
                left: place.left,
                width: place.width,
                '--arrow-x': `${place.arrow}px`,
              } as React.CSSProperties)
            : undefined
        }
        onClick={stop}
      >
        <b className={styles.tipTitle}>{`${LABEL}.`}</b> {EXPLAIN}
      </span>
    </span>
  )
}

export default VerifiedCheck
